/**
 * IndexedDB Storage Utility
 * 
 * Provides a simple key-value storage API using IndexedDB for storing large objects
 * that exceed localStorage quota limits.
 * 
 * Usage:
 * - Direct: import indexedDbStorage from './utils/indexedDbStorage'
 * - Unified: import storageManager from './utils/storageManager' (recommended)
 * 
 * Features:
 * - Handles large data structures (configuration, collections, files)
 * - Asynchronous API with Promises
 * - Automatic migration from localStorage
 * - Fallback to localStorage for small data
 * - Error handling and quota management
 */

const DB_NAME = 'CanvasStorage';
const DB_VERSION = 1;
const STORE_NAME = 'canvasData';

class IndexedDbStorage {
  constructor() {
    this.db = null;
    this.isSupported = typeof indexedDB !== 'undefined';
  }

  /**
   * Initialize the database connection
   * @returns {Promise<IDBDatabase>}
   */
  async init() {
    if (this.db) {
      return this.db;
    }

    if (!this.isSupported) {
      console.warn('IndexedDB is not supported in this browser'); /* eslint-disable-line no-console */
      return null;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error); /* eslint-disable-line no-console */
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully'); /* eslint-disable-line no-console */
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
          console.log('Created object store:', STORE_NAME); /* eslint-disable-line no-console */
        }
      };
    });
  }

  /**
   * Store data in IndexedDB
   * @param {string} key - Storage key
   * @param {any} value - Value to store (will be serialized)
   * @returns {Promise<void>}
   */
  async setItem(key, value) {
    try {
      await this.init();

      if (!this.db) {
        // Fallback to localStorage
        console.warn('IndexedDB not available, falling back to localStorage'); /* eslint-disable-line no-console */
        try {
          localStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
          console.error('Failed to save to localStorage:', e); /* eslint-disable-line no-console */
          throw new Error('Storage quota exceeded and IndexedDB unavailable');
        }
        return;
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(value, key);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error('Failed to store data in IndexedDB:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };

        transaction.onerror = () => {
          console.error('Transaction failed:', transaction.error); /* eslint-disable-line no-console */
          reject(transaction.error);
        };
      });
    } catch (error) {
      console.error('Error in setItem:', error); /* eslint-disable-line no-console */
      throw error;
    }
  }

  /**
   * Retrieve data from IndexedDB
   * @param {string} key - Storage key
   * @returns {Promise<any|null>} The stored value or null if not found
   */
  async getItem(key) {
    try {
      await this.init();

      if (!this.db) {
        // Fallback to localStorage
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
          resolve(request.result || null);
        };

        request.onerror = () => {
          console.error('Failed to retrieve data from IndexedDB:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error in getItem:', error); /* eslint-disable-line no-console */
      return null;
    }
  }

  /**
   * Remove data from IndexedDB
   * @param {string} key - Storage key
   * @returns {Promise<void>}
   */
  async removeItem(key) {
    try {
      await this.init();

      if (!this.db) {
        // Fallback to localStorage
        localStorage.removeItem(key);
        return;
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(key);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error('Failed to remove data from IndexedDB:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error in removeItem:', error); /* eslint-disable-line no-console */
    }
  }

  /**
   * Clear all data from the store
   * @returns {Promise<void>}
   */
  async clear() {
    try {
      await this.init();

      if (!this.db) {
        return;
      }

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error('Failed to clear IndexedDB:', request.error); /* eslint-disable-line no-console */
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error in clear:', error); /* eslint-disable-line no-console */
    }
  }

  /**
   * Migrate data from localStorage to IndexedDB
   * @param {string} key - Storage key to migrate
   * @returns {Promise<boolean>} True if migration was performed
   */
  async migrateFromLocalStorage(key) {
    try {
      const localData = localStorage.getItem(key);
      
      if (!localData) {
        return false;
      }

      console.log(`Migrating ${key} from localStorage to IndexedDB`); /* eslint-disable-line no-console */
      const parsedData = JSON.parse(localData);
      await this.setItem(key, parsedData);
      
      // Remove from localStorage after successful migration
      localStorage.removeItem(key);
      console.log(`Successfully migrated ${key} to IndexedDB`); /* eslint-disable-line no-console */
      
      return true;
    } catch (error) {
      console.error('Error migrating from localStorage:', error); /* eslint-disable-line no-console */
      return false;
    }
  }

  /**
   * Check if IndexedDB is supported and available
   * @returns {boolean}
   */
  isAvailable() {
    return this.isSupported;
  }
}

// Create and export a singleton instance
const indexedDbStorage = new IndexedDbStorage();

export default indexedDbStorage;
